import { rd, type RemoteData } from '@passionware/monads';
import { useSelector } from 'react-redux';
import type { Booking, Resource } from '__types__';
import { MergeServices } from 'v5/platform/ts/services';
import { WithBookingService } from 'v5/services/io/BookingService/BookingService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';

export type ConfirmationRecipient = {
  bookingId: number;
  contactId: number;
  resource: Resource;
  emails: string[];
};

export type ConfirmationRecipientProblem = ConfirmationRecipient & {
  problem: 'missing_email';
};

export type ConfirmationRecipientList = {
  ready: ConfirmationRecipient[];
  problems: ConfirmationRecipientProblem[];
};

export type BookingStatusCatalogEntry = {
  status_type?: string | null;
};

export type BookingStatusCatalog = Record<string, BookingStatusCatalogEntry>;

export type ConfirmationEligibility = 'confirmed' | 'ineligible' | 'unknown';

type BookingWithWireStatus = Booking & { status_type?: string | null };

export function recipientEmailsFromResource(resource: Resource): string[] {
  return (resource.emails ?? [])
    .map(email => email.value_1?.trim())
    .filter((value): value is string => Boolean(value));
}

function statusTypeFromBooking(booking: Booking): string | undefined {
  const raw = (booking as BookingWithWireStatus).status_type;
  if (raw == null || String(raw).trim() === '') {
    return undefined;
  }
  return String(raw).toUpperCase();
}

/**
 * Confirmed (and unsent, with a contact) vs skip vs “cannot decide yet”.
 * Wire `status_type` wins. Catalog lookup only if the booking has no type;
 * an empty catalog is unknown, not “not confirmed”.
 */
export function confirmationEligibility(
  booking: Booking,
  catalog: BookingStatusCatalog | undefined
): ConfirmationEligibility {
  if (booking.contact_id == null || booking.confirmation_sent_at != null) {
    return 'ineligible';
  }

  const fromBooking = statusTypeFromBooking(booking);
  if (fromBooking != null) {
    return fromBooking === 'CONFIRMED' ? 'confirmed' : 'ineligible';
  }

  if (booking.status_id == null) {
    return 'ineligible';
  }

  const catalogLoaded = catalog != null && Object.keys(catalog).length > 0;
  if (!catalogLoaded) {
    return 'unknown';
  }

  const entry = catalog[String(booking.status_id)];
  if (entry?.status_type == null) {
    return 'ineligible';
  }

  return String(entry.status_type).toUpperCase() === 'CONFIRMED'
    ? 'confirmed'
    : 'ineligible';
}

export function buildConfirmationRecipientList(
  bookings: Booking[],
  resources: Resource[]
): ConfirmationRecipientList {
  const resourceById = new Map(
    resources.map(resource => [resource.id, resource] as const)
  );
  const ready: ConfirmationRecipient[] = [];
  const problems: ConfirmationRecipientProblem[] = [];

  for (const booking of bookings) {
    if (booking.contact_id == null) {
      continue;
    }
    const resource = resourceById.get(booking.contact_id);
    if (resource == null) {
      continue;
    }
    const emails = recipientEmailsFromResource(resource);
    const row: ConfirmationRecipient = {
      bookingId: booking.id,
      contactId: booking.contact_id,
      resource,
      emails
    };
    if (emails.length === 0) {
      problems.push({ ...row, problem: 'missing_email' });
    } else {
      ready.push(row);
    }
  }

  return { ready, problems };
}

/**
 * Confirmed, not-yet-sent bookings from the given ids, split into ready (has
 * email) and problem rows (e.g. missing email).
 */
export function useProductionConfirmationRecipients(
  bookingIds: number[],
  services: MergeServices<[WithBookingService, WithResourceService]>
): RemoteData<ConfirmationRecipientList> {
  const statuses = useSelector(
    (state: { statuses?: { data?: BookingStatusCatalog } }) =>
      state.statuses?.data
  );

  const bookingsById = services.bookingService.useBookingsById(bookingIds);
  const bookingsRd = rd.useCombine(bookingsById);

  const confirmedBookingsRd = rd.useMemoMapMonadic(bookingsRd, bookings => {
    const list = Object.values(bookings).filter(
      (booking): booking is Booking => booking != null
    );
    if (
      list.some(
        booking => confirmationEligibility(booking, statuses) === 'unknown'
      )
    ) {
      return rd.ofIdle();
    }
    return rd.of(
      list.filter(
        booking => confirmationEligibility(booking, statuses) === 'confirmed'
      )
    );
  });

  const contactIds = rd.mapOrElse(
    confirmedBookingsRd,
    bookings =>
      Array.from(
        new Set(
          bookings
            .map(booking => booking.contact_id)
            .filter((id): id is number => id != null)
        )
      ),
    [] as number[]
  );

  const resourcesRd = services.resourceService.useItemsById(contactIds);

  return rd.useMemoMapMonadic(
    rd.useCombine({
      bookings: confirmedBookingsRd,
      resources: resourcesRd
    }),
    ({ bookings, resources }) =>
      rd.of(buildConfirmationRecipientList(bookings, resources))
  );
}
